import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import {
  AlertState,
  Cycle,
  FinanceAccountType,
  FinanceCategoryLevel,
  FinanceKeywordScope,
  FinanceOverview,
  FinanceTransaction,
  FinanceTransactionType,
} from '../types';
import './Finance.css';

type FinanceTab =
  | 'transactions'
  | 'accounts'
  | 'banks'
  | 'sources'
  | 'categories'
  | 'tags'
  | 'keywords';

interface TransactionFormData {
  type: FinanceTransactionType;
  description: string;
  amount: string;
  transactionDate: string;
  cycleId: string;
  accountId: string;
  bankId: string;
  sourceId: string;
  categoryId: string;
  notes: string;
  tagIds: string[];
  existingAttachmentName: string | null;
  removeAttachment: boolean;
  /** Lançamento espelhado da folha: só metadados (conta, categoria, etc.) podem ser alterados aqui. */
  isPayrollMirror: boolean;
}

interface AccountFormData {
  name: string;
  type: FinanceAccountType;
  description: string;
  active: boolean;
}

interface BankFormData {
  name: string;
  code: string;
  accountNumber: string;
  description: string;
  active: boolean;
}

interface SourceFormData {
  name: string;
  type: FinanceTransactionType;
  description: string;
  active: boolean;
}

interface CategoryFormData {
  name: string;
  type: FinanceTransactionType;
  level: FinanceCategoryLevel;
  parentId: string;
  active: boolean;
}

interface TagFormData {
  name: string;
  color: string;
  active: boolean;
}

interface KeywordFormData {
  term: string;
  scope: FinanceKeywordScope;
  categoryId: string;
  sourceId: string;
  tagId: string;
  active: boolean;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
}

const financeTabLabels: Record<FinanceTab, string> = {
  transactions: 'Lançamentos',
  accounts: 'Contas',
  banks: 'Bancos',
  sources: 'Fontes',
  categories: 'Categorias',
  tags: 'Tags',
  keywords: 'Palavras-chave',
};

const sharedFinanceTabs = new Set<FinanceTab>([
  'transactions',
  'accounts',
  'banks',
  'sources',
  'categories',
]);

const transactionTypeLabels: Record<FinanceTransactionType, string> = {
  income: 'Receita',
  expense: 'Despesa',
  tithe: 'Dízimo',
};

const financeTypeOptions = [
  { value: 'expense' as const, label: 'Despesa' },
  { value: 'income' as const, label: 'Receita' },
  { value: 'tithe' as const, label: 'Dízimo' },
];

const transactionApprovalLabels = {
  pending: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Recusado',
} as const;

const accountTypeLabels: Record<FinanceAccountType, string> = {
  cash: 'Caixa',
  bank: 'Banco',
  card: 'Cartão',
  digital: 'Digital',
  other: 'Outro',
};

const categoryLevelLabels: Record<FinanceCategoryLevel, string> = {
  macro: 'Macro',
  medium: 'Médio',
  micro: 'Micro',
};

const keywordScopeLabels: Record<FinanceKeywordScope, string> = {
  both: 'Ambos',
  income: 'Receitas',
  expense: 'Despesas',
};

const createEmptyTransactionForm = (): TransactionFormData => ({
  type: 'expense',
  description: '',
  amount: '',
  transactionDate: '',
  cycleId: '',
  accountId: '',
  bankId: '',
  sourceId: '',
  categoryId: '',
  notes: '',
  tagIds: [],
  existingAttachmentName: null,
  removeAttachment: false,
  isPayrollMirror: false,
});

const createEmptyAccountForm = (): AccountFormData => ({
  name: '',
  type: 'bank',
  description: '',
  active: true,
});

const createEmptyBankForm = (): BankFormData => ({
  name: '',
  code: '',
  accountNumber: '',
  description: '',
  active: true,
});

const createEmptySourceForm = (): SourceFormData => ({
  name: '',
  type: 'expense',
  description: '',
  active: true,
});

const createEmptyCategoryForm = (): CategoryFormData => ({
  name: '',
  type: 'expense',
  level: 'macro',
  parentId: '',
  active: true,
});

const createEmptyTagForm = (): TagFormData => ({
  name: '',
  color: '#2563eb',
  active: true,
});

const createEmptyKeywordForm = (): KeywordFormData => ({
  term: '',
  scope: 'both',
  categoryId: '',
  sourceId: '',
  tagId: '',
  active: true,
});

const normalizeDateInput = (value?: string | null): string =>
  value ? value.slice(0, 10) : '';

const formatDate = (value?: string | null): string => {
  if (!value) return 'Não informado';
  return new Date(`${normalizeDateInput(value)}T00:00:00`).toLocaleDateString('pt-BR');
};

const formatCurrency = (value: number | string): string =>
  Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatAttachmentFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getTransactionYear = (transaction: FinanceTransaction): string =>
  normalizeDateInput(transaction.transactionDate).slice(0, 4);

const getTransactionPeriod = (transaction: FinanceTransaction): string =>
  transaction.cycle?.id || normalizeDateInput(transaction.transactionDate).slice(0, 7);

const getCycleLabel = (cycle?: Cycle | null, transactionDate?: string): string => {
  if (cycle?.name) return cycle.name;
  if (!transactionDate) return 'Sem período';

  const date = new Date(`${normalizeDateInput(transactionDate)}T00:00:00`);
  const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};

const toNullableValue = (value: string): string | null => (value ? value : null);

const isTransactionApproved = (transaction: FinanceTransaction): boolean =>
  !transaction.approvalStatus || transaction.approvalStatus === 'approved';

const isTransactionPending = (transaction: FinanceTransaction): boolean =>
  transaction.approvalStatus === 'pending';

const PAYROLL_EARNING_BUCKETS = new Set([
  'salaries',
  'thirteenth',
  'vacation',
  'other',
]);

const getTransactionDisplayType = (
  transaction: FinanceTransaction,
  isFinanceAdmin: boolean,
): FinanceTransactionType => {
  if (transaction.displayType) {
    return transaction.displayType;
  }
  if (
    !isFinanceAdmin &&
    transaction.payrollLineId &&
    transaction.payrollLineBucket &&
    PAYROLL_EARNING_BUCKETS.has(transaction.payrollLineBucket)
  ) {
    return 'income';
  }
  if (
    !isFinanceAdmin &&
    transaction.payrollLineId &&
    transaction.payrollLineBucket &&
    transaction.payrollLineBucket === 'withholdings'
  ) {
    return 'expense';
  }
  return transaction.type;
};

const Finance: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, loading: authLoading } = authContext || {};
  const [overview, setOverview] = useState<FinanceOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<FinanceTab>('transactions');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<'all' | FinanceTransactionType>('all');
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);

  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [transactionForm, setTransactionForm] = useState<TransactionFormData>(
    createEmptyTransactionForm(),
  );
  const [transactionAttachmentFile, setTransactionAttachmentFile] = useState<File | null>(null);
  const transactionAttachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentDragDepthRef = useRef(0);
  const [attachmentDropzoneActive, setAttachmentDropzoneActive] = useState(false);

  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState<AccountFormData>(createEmptyAccountForm());

  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const [bankForm, setBankForm] = useState<BankFormData>(createEmptyBankForm());

  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [sourceForm, setSourceForm] = useState<SourceFormData>(createEmptySourceForm());

  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>(createEmptyCategoryForm());

  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagForm, setTagForm] = useState<TagFormData>(createEmptyTagForm());

  const [editingKeywordId, setEditingKeywordId] = useState<string | null>(null);
  const [keywordForm, setKeywordForm] = useState<KeywordFormData>(createEmptyKeywordForm());
  const transactionSectionRef = useRef<HTMLElement | null>(null);
  const accountSectionRef = useRef<HTMLElement | null>(null);
  const bankSectionRef = useRef<HTMLElement | null>(null);
  const sourceSectionRef = useRef<HTMLElement | null>(null);
  const categorySectionRef = useRef<HTMLElement | null>(null);
  const tagSectionRef = useRef<HTMLElement | null>(null);
  const keywordSectionRef = useRef<HTMLElement | null>(null);

  const loadOverview = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.get<FinanceOverview>('/finance/overview');
      setOverview(response.data);
    } catch (error) {
      console.error('Erro ao carregar visão financeira:', error);
      setAlert({
        isOpen: true,
        message: 'Erro ao carregar os dados financeiros',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !authLoading) {
      loadOverview();
    }
  }, [authLoading, loadOverview, user]);

  const isFinanceAdmin = user?.role === 'admin';

  const resolveTransactionDisplayType = useCallback(
    (transaction: FinanceTransaction) => getTransactionDisplayType(transaction, isFinanceAdmin),
    [isFinanceAdmin],
  );

  const cycles = useMemo(() => overview?.cycles ?? [], [overview]);
  const accounts = useMemo(() => overview?.accounts ?? [], [overview]);
  const banks = useMemo(() => overview?.banks ?? [], [overview]);
  const sources = useMemo(() => overview?.sources ?? [], [overview]);
  const categories = useMemo(() => overview?.categories ?? [], [overview]);
  const tags = useMemo(() => overview?.tags ?? [], [overview]);
  const keywords = useMemo(() => overview?.keywords ?? [], [overview]);
  const transactions = useMemo(() => {
    const raw = overview?.transactions ?? [];
    const byId = new Map<string, FinanceTransaction>();
    for (const transaction of raw) {
      if (!byId.has(transaction.id)) {
        byId.set(transaction.id, transaction);
      }
    }
    return Array.from(byId.values());
  }, [overview]);

  const visibleFinanceTabEntries = useMemo(
    () =>
      Object.entries(financeTabLabels).filter(
        ([key]) => isFinanceAdmin || sharedFinanceTabs.has(key as FinanceTab),
      ),
    [isFinanceAdmin],
  );

  useEffect(() => {
    if (!isFinanceAdmin && !sharedFinanceTabs.has(activeTab)) {
      setActiveTab('transactions');
    }
  }, [activeTab, isFinanceAdmin]);

  const availableYears = useMemo(
    () =>
      Array.from(new Set(transactions.map((transaction) => getTransactionYear(transaction)))).sort(
        (a, b) => Number(b) - Number(a),
      ),
    [transactions],
  );

  const availablePeriods = useMemo(() => {
    const items = new Map<string, string>();
    transactions.forEach((transaction) => {
      if (selectedYear !== 'all' && getTransactionYear(transaction) !== selectedYear) {
        return;
      }
      const key = getTransactionPeriod(transaction);
      items.set(key, getCycleLabel(transaction.cycle, transaction.transactionDate));
    });
    return Array.from(items.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => b.label.localeCompare(a.label));
  }, [selectedYear, transactions]);

  useEffect(() => {
    if (
      selectedPeriod !== 'all' &&
      !availablePeriods.some((period) => period.value === selectedPeriod)
    ) {
      setSelectedPeriod('all');
    }
  }, [availablePeriods, selectedPeriod]);

  const filteredTransactions = useMemo(
    () =>
      transactions.filter((transaction) => {
        const matchesYear =
          selectedYear === 'all' || getTransactionYear(transaction) === selectedYear;
        const matchesPeriod =
          selectedPeriod === 'all' || getTransactionPeriod(transaction) === selectedPeriod;
        const matchesType =
          selectedType === 'all' ||
          resolveTransactionDisplayType(transaction) === selectedType;
        return matchesYear && matchesPeriod && matchesType;
      }),
    [resolveTransactionDisplayType, selectedPeriod, selectedType, selectedYear, transactions],
  );

  const filteredSummary = useMemo(
    () =>
      filteredTransactions.filter(isTransactionApproved).reduce(
        (acc, transaction) => {
          const amount = Number(transaction.amount);
          const displayType = resolveTransactionDisplayType(transaction);
          if (displayType === 'income') {
            acc.totalIncome += amount;
          } else {
            acc.totalExpense += amount;
          }
          acc.balance = acc.totalIncome - acc.totalExpense;
          acc.transactionsCount += 1;
          return acc;
        },
        { totalIncome: 0, totalExpense: 0, balance: 0, transactionsCount: 0 },
      ),
    [filteredTransactions, resolveTransactionDisplayType],
  );

  const groupedTransactions = useMemo(() => {
    const groups = new Map<
      string,
      {
        label: string;
        transactions: FinanceTransaction[];
      }
    >();

    filteredTransactions.forEach((transaction) => {
      const key = getTransactionPeriod(transaction);
      const current = groups.get(key) || {
        label: getCycleLabel(transaction.cycle, transaction.transactionDate),
        transactions: [],
      };
      current.transactions.push(transaction);
      groups.set(key, current);
    });

    return Array.from(groups.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [filteredTransactions]);

  const sourceOptions = useMemo(
    () => sources.filter((source) => source.type === transactionForm.type),
    [sources, transactionForm.type],
  );

  const categoryOptions = useMemo(
    () => categories.filter((category) => category.type === transactionForm.type),
    [categories, transactionForm.type],
  );

  const categoryParentOptions = useMemo(() => {
    if (categoryForm.level === 'macro') return [];
    if (categoryForm.level === 'medium') {
      return categories.filter(
        (category) => category.type === categoryForm.type && category.level === 'macro',
      );
    }
    return categories.filter(
      (category) => category.type === categoryForm.type && category.level === 'medium',
    );
  }, [categories, categoryForm.level, categoryForm.type]);

  const keywordScopedCategories = useMemo(() => {
    if (keywordForm.scope === 'both') return categories;
    return categories.filter((category) => category.type === keywordForm.scope);
  }, [categories, keywordForm.scope]);

  const keywordScopedSources = useMemo(() => {
    if (keywordForm.scope === 'both') return sources;
    return sources.filter((source) => source.type === keywordForm.scope);
  }, [keywordForm.scope, sources]);

  const financeFilterYearOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      ...availableYears.map((y) => ({ value: y, label: y })),
    ],
    [availableYears],
  );

  const financeFilterPeriodOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      ...availablePeriods.map((p) => ({ value: p.value, label: p.label })),
    ],
    [availablePeriods],
  );

  const financeFilterTypeOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'income', label: 'Receitas' },
      { value: 'expense', label: 'Despesas' },
      { value: 'tithe', label: 'Dízimo' },
    ],
    [],
  );

  const activeStatusOptions = useMemo(
    () => [
      { value: 'true', label: 'Ativa' },
      { value: 'false', label: 'Inativa' },
    ],
    [],
  );

  const transactionFormTypeOptions = useMemo(() => financeTypeOptions, []);

  const cycleFormSelectOptions = useMemo(
    () => [
      { value: '', label: 'Sem vínculo' },
      ...cycles.map((c) => ({ value: c.id, label: c.name })),
    ],
    [cycles],
  );

  const accountPickOptions = useMemo(
    () => [
      { value: '', label: 'Não informado' },
      ...accounts.map((a) => ({ value: a.id, label: a.name })),
    ],
    [accounts],
  );

  const bankPickOptions = useMemo(
    () => [
      { value: '', label: 'Não informado' },
      ...banks.map((b) => {
        const parts = [b.name];
        if (b.code) parts.push(b.code);
        if (b.accountNumber) parts.push(`Cc ${b.accountNumber}`);
        return { value: b.id, label: parts.join(' • ') };
      }),
    ],
    [banks],
  );

  const sourcePickOptions = useMemo(
    () => [
      { value: '', label: 'Não informado' },
      ...sourceOptions.map((s) => ({ value: s.id, label: s.name })),
    ],
    [sourceOptions],
  );

  const categoryPickOptions = useMemo(
    () => [
      { value: '', label: 'Não informado' },
      ...categoryOptions.map((c) => ({
        value: c.id,
        label: `${c.name} (${categoryLevelLabels[c.level]})`,
      })),
    ],
    [categoryOptions],
  );

  const categoryParentPickOptions = useMemo(
    () => [
      { value: '', label: 'Selecione' },
      ...categoryParentOptions.map((c) => ({ value: c.id, label: c.name })),
    ],
    [categoryParentOptions],
  );

  const accountTypeSelectOptions = useMemo(
    () => Object.entries(accountTypeLabels).map(([value, label]) => ({ value, label })),
    [],
  );

  const keywordScopeSelectOptions = useMemo(
    () => Object.entries(keywordScopeLabels).map(([value, label]) => ({ value, label })),
    [],
  );

  const keywordCategoryPickOptions = useMemo(
    () => [
      { value: '', label: 'Nenhuma' },
      ...keywordScopedCategories.map((c) => ({ value: c.id, label: c.name })),
    ],
    [keywordScopedCategories],
  );

  const keywordSourcePickOptions = useMemo(
    () => [
      { value: '', label: 'Nenhuma' },
      ...keywordScopedSources.map((s) => ({ value: s.id, label: s.name })),
    ],
    [keywordScopedSources],
  );

  const keywordTagPickOptions = useMemo(
    () => [
      { value: '', label: 'Nenhuma' },
      ...tags.map((t) => ({ value: t.id, label: t.name })),
    ],
    [tags],
  );

  const categoryLevelSelectOptions = useMemo(
    () => Object.entries(categoryLevelLabels).map(([value, label]) => ({ value, label })),
    [],
  );

  const scrollToSection = (section: HTMLElement | null, extraOffset = 40): void => {
    if (!section) return;

    const run = (): void => {
      const header = document.querySelector('.app-header');
      const headerHeight = header?.getBoundingClientRect().height ?? 96;
      const top =
        section.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
  };

  const focusTransactionEditor = (): void => {
    setActiveTab('transactions');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToSection(transactionSectionRef.current, 48);
      });
    });
  };

  const resetTransactionForm = (): void => {
    setEditingTransactionId(null);
    setTransactionForm(createEmptyTransactionForm());
    setTransactionAttachmentFile(null);
    attachmentDragDepthRef.current = 0;
    setAttachmentDropzoneActive(false);
    if (transactionAttachmentInputRef.current) {
      transactionAttachmentInputRef.current.value = '';
    }
  };

  const resetAccountForm = (): void => {
    setEditingAccountId(null);
    setAccountForm(createEmptyAccountForm());
  };

  const resetBankForm = (): void => {
    setEditingBankId(null);
    setBankForm(createEmptyBankForm());
  };

  const resetSourceForm = (): void => {
    setEditingSourceId(null);
    setSourceForm(createEmptySourceForm());
  };

  const resetCategoryForm = (): void => {
    setEditingCategoryId(null);
    setCategoryForm(createEmptyCategoryForm());
  };

  const resetTagForm = (): void => {
    setEditingTagId(null);
    setTagForm(createEmptyTagForm());
  };

  const resetKeywordForm = (): void => {
    setEditingKeywordId(null);
    setKeywordForm(createEmptyKeywordForm());
  };

  const handleMutationError = (error: any, fallbackMessage: string): void => {
    const data = error.response?.data;
    let message = fallbackMessage;
    if (typeof data?.message === 'string') {
      message = data.message;
    } else if (Array.isArray(data?.message)) {
      message = data.message.join(', ');
    }
    setAlert({
      isOpen: true,
      message,
      type: 'error',
    });
  };

  const buildTransactionFormData = (): FormData => {
    const fd = new FormData();
    fd.append('type', transactionForm.type);
    fd.append('description', transactionForm.description);
    fd.append('amount', String(Number(transactionForm.amount)));
    fd.append('transactionDate', transactionForm.transactionDate);
    const cycleId = toNullableValue(transactionForm.cycleId);
    if (cycleId) fd.append('cycleId', cycleId);
    const accountId = toNullableValue(transactionForm.accountId);
    if (accountId) fd.append('accountId', accountId);
    const bankId = toNullableValue(transactionForm.bankId);
    if (bankId) fd.append('bankId', bankId);
    const sourceId = toNullableValue(transactionForm.sourceId);
    if (sourceId) fd.append('sourceId', sourceId);
    const categoryId = toNullableValue(transactionForm.categoryId);
    if (categoryId) fd.append('categoryId', categoryId);
    const notesTrimmed = transactionForm.notes.trim();
    if (notesTrimmed) fd.append('notes', notesTrimmed);
    fd.append('tagIds', JSON.stringify(transactionForm.tagIds));
    if (editingTransactionId && transactionForm.removeAttachment) {
      fd.append('removeAttachment', 'true');
    }
    if (transactionAttachmentFile) {
      fd.append('attachment', transactionAttachmentFile);
    }
    return fd;
  };

  const downloadTransactionAttachment = async (
    transactionId: string,
    fallbackName: string,
  ): Promise<void> => {
    try {
      const res = await api.get(`/finance/transactions/${transactionId}/attachment`, {
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fallbackName || 'comprovante';
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      handleMutationError(error, 'Não foi possível baixar o comprovante');
    }
  };

  const handleTransactionSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (transactionForm.isPayrollMirror && !editingTransactionId) {
      return;
    }
    const wasEditing = Boolean(editingTransactionId);
    const savedPayrollClassify = transactionForm.isPayrollMirror;
    const formData = buildTransactionFormData();

    try {
      if (editingTransactionId) {
        await api.patch(`/finance/transactions/${editingTransactionId}`, formData);
      } else {
        await api.post('/finance/transactions', formData);
      }
      resetTransactionForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: savedPayrollClassify
          ? 'Classificação salva. Valor e descrição continuam vindo da folha de pagamento.'
          : wasEditing
            ? 'Lançamento atualizado com sucesso!'
            : isFinanceAdmin
              ? 'Lançamento criado com sucesso!'
              : 'Lançamento enviado para aprovação do administrador!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar lançamento');
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      if (editingAccountId) {
        await api.patch(`/finance/accounts/${editingAccountId}`, accountForm);
      } else {
        await api.post('/finance/accounts', accountForm);
      }
      resetAccountForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingAccountId ? 'Conta atualizada com sucesso!' : 'Conta criada com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar conta');
    }
  };

  const handleBankSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const payload = {
      name: bankForm.name.trim(),
      code: bankForm.code.trim() || undefined,
      accountNumber: bankForm.accountNumber.trim() || undefined,
      description: bankForm.description.trim() || undefined,
      active: bankForm.active,
    };
    try {
      if (editingBankId) {
        await api.patch(`/finance/banks/${editingBankId}`, payload);
      } else {
        await api.post('/finance/banks', payload);
      }
      resetBankForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingBankId ? 'Banco atualizado com sucesso!' : 'Banco cadastrado com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar banco');
    }
  };

  const handleSourceSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      if (editingSourceId) {
        await api.patch(`/finance/sources/${editingSourceId}`, sourceForm);
      } else {
        await api.post('/finance/sources', sourceForm);
      }
      resetSourceForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingSourceId ? 'Fonte atualizada com sucesso!' : 'Fonte criada com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar fonte');
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const payload = {
      ...categoryForm,
      parentId: toNullableValue(categoryForm.parentId),
    };
    try {
      if (editingCategoryId) {
        await api.patch(`/finance/categories/${editingCategoryId}`, payload);
      } else {
        await api.post('/finance/categories', payload);
      }
      resetCategoryForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingCategoryId
          ? 'Categoria atualizada com sucesso!'
          : 'Categoria criada com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar categoria');
    }
  };

  const handleTagSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      if (editingTagId) {
        await api.patch(`/finance/tags/${editingTagId}`, tagForm);
      } else {
        await api.post('/finance/tags', tagForm);
      }
      resetTagForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingTagId ? 'Tag atualizada com sucesso!' : 'Tag criada com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar tag');
    }
  };

  const handleKeywordSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const payload = {
      ...keywordForm,
      categoryId: toNullableValue(keywordForm.categoryId),
      sourceId: toNullableValue(keywordForm.sourceId),
      tagId: toNullableValue(keywordForm.tagId),
    };
    try {
      if (editingKeywordId) {
        await api.patch(`/finance/keywords/${editingKeywordId}`, payload);
      } else {
        await api.post('/finance/keywords', payload);
      }
      resetKeywordForm();
      await loadOverview();
      setAlert({
        isOpen: true,
        message: editingKeywordId
          ? 'Palavra-chave atualizada com sucesso!'
          : 'Palavra-chave criada com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao salvar palavra-chave');
    }
  };

  const handleDelete = async (
    path: string,
    label: string,
    successMessage: string,
    afterDelete?: () => void,
  ): Promise<void> => {
    setConfirmDialog({
      isOpen: true,
      title: 'Confirmar exclusão',
      message: `Deseja realmente excluir ${label}?`,
      onConfirm: async () => {
        try {
          await api.delete(path);
          afterDelete?.();
          await loadOverview();
          setAlert({
            isOpen: true,
            message: successMessage,
            type: 'success',
          });
        } catch (error: any) {
          handleMutationError(error, `Erro ao excluir ${label}`);
        }
      },
    });
  };

  const handleApproveTransaction = async (transaction: FinanceTransaction): Promise<void> => {
    try {
      await api.post(`/finance/transactions/${transaction.id}/approve`);
      await loadOverview();
      setAlert({
        isOpen: true,
        message: 'Lançamento aprovado com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao aprovar lançamento');
    }
  };

  const handleRejectTransaction = async (transaction: FinanceTransaction): Promise<void> => {
    try {
      await api.post(`/finance/transactions/${transaction.id}/reject`);
      await loadOverview();
      setAlert({
        isOpen: true,
        message: 'Lançamento recusado.',
        type: 'success',
      });
    } catch (error: any) {
      handleMutationError(error, 'Erro ao recusar lançamento');
    }
  };

  const closeConfirmDialog = (): void => {
    if (isConfirmingDelete) return;
    setConfirmDialog(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!confirmDialog) return;
    setIsConfirmingDelete(true);
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } finally {
      setIsConfirmingDelete(false);
    }
  };

  if (!user || authLoading || loading) {
    return (
      <div className="finance-state">
        <div className="finance-state-card">
          <span className="finance-badge">Carregando</span>
          <h1>Preparando financeiro</h1>
          <p>Buscando lançamentos, cadastros auxiliares e visão consolidada da operação.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-page">
      <section className="finance-hero">
        <div className="finance-hero-copy">
          <span className="finance-badge">
            {isFinanceAdmin ? 'Gestão financeira' : 'Área financeira'}
          </span>
          <h1>
            {isFinanceAdmin ? 'Receitas, despesas e classificações' : 'Lançamentos e classificações'}
          </h1>
          <p>
            {isFinanceAdmin
              ? 'Cadastre lançamentos, organize contas, fontes, categorias macro/médio/micro, tags e palavras-chave com visão consolidada por período.'
              : 'Cadastre e acompanhe seus lançamentos, contas, fontes e categorias com o mesmo padrão premium da gestão financeira.'}
          </p>
        </div>

        <div className="finance-hero-grid">
          <article className="finance-hero-card">
            <span>Receitas</span>
            <strong>{formatCurrency(filteredSummary.totalIncome)}</strong>
          </article>
          <article className="finance-hero-card">
            <span>Despesas</span>
            <strong>{formatCurrency(filteredSummary.totalExpense)}</strong>
          </article>
          <article className="finance-hero-card">
            <span>Saldo</span>
            <strong>{formatCurrency(filteredSummary.balance)}</strong>
          </article>
        </div>
      </section>

      <section className="finance-surface">
        <div className="finance-section-header">
          <div>
            <span className="finance-section-kicker">Filtros</span>
            <h2>Refine por período</h2>
          </div>
          <p className="finance-section-lead">
            {filteredSummary.transactionsCount} lançamento(s) no recorte atual.
          </p>
        </div>

        <div className="finance-filter-grid premium-filters">
          <PremiumSelect
            id="finance-filter-year"
            label="Ano"
            wrapperClassName="premium-select-field"
            value={selectedYear}
            options={financeFilterYearOptions}
            onChange={setSelectedYear}
            placeholder="Todos"
          />

          <PremiumSelect
            id="finance-filter-period"
            label="Período"
            wrapperClassName="premium-select-field"
            value={selectedPeriod}
            options={financeFilterPeriodOptions}
            onChange={setSelectedPeriod}
            placeholder="Todos"
          />

          <PremiumSelect
            id="finance-filter-type"
            label="Tipo"
            wrapperClassName="premium-select-field"
            value={selectedType}
            options={financeFilterTypeOptions}
            onChange={(value) => setSelectedType(value as 'all' | FinanceTransactionType)}
            placeholder="Todos"
          />
          <button
            type="button"
            className="premium-filter-reset"
            onClick={() => {
              setSelectedYear('all');
              setSelectedPeriod('all');
              setSelectedType('all');
            }}
          >
            Limpar filtros
          </button>
        </div>
      </section>

      <nav className="premium-segment-tabs" aria-label="Seções do financeiro">
        {visibleFinanceTabEntries.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`premium-segment-tab${activeTab === key ? ' is-active' : ''}`}
            onClick={() => setActiveTab(key as FinanceTab)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="premium-tab-panel" hidden={activeTab !== 'transactions'}>
          <section ref={transactionSectionRef} className="finance-surface finance-scroll-target">
            <div className="finance-section-header">
              <div>
                <span className="finance-section-kicker">Lançamentos</span>
                <h2>
                  {transactionForm.isPayrollMirror
                    ? 'Classificar lançamento da folha'
                    : editingTransactionId
                      ? 'Editar lançamento'
                      : 'Novo lançamento'}
                </h2>
              </div>
              <p>
                {transactionForm.isPayrollMirror
                  ? 'Vincule período, conta, banco, fonte, categoria, observações e tags para relatórios. Valor, data e descrição vêm da folha de pagamento e não podem ser alterados aqui.'
                  : 'Registre receitas e despesas, associe período, conta, banco, fonte, categoria e tags.'}
              </p>
            </div>

            <form className="finance-form" onSubmit={handleTransactionSubmit}>
              {transactionForm.isPayrollMirror && (
                <p className="finance-payroll-classify-banner" role="status">
                  <strong>Lançamento da folha de pagamento.</strong> Para mudar valor ou rubrica,
                  use a aba <Link to="/financeiro/folha">Folha de pagamento</Link>. Abaixo você só
                  complementa a classificação para relatórios.
                </p>
              )}
              <div className="finance-form-grid finance-form-grid-4">
                <PremiumSelect
                  label="Tipo"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.type}
                  options={transactionFormTypeOptions}
                  disabled={transactionForm.isPayrollMirror}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      type: value as FinanceTransactionType,
                      sourceId: '',
                      categoryId: '',
                    }))
                  }
                />
                <div className="form-group finance-form-span-2">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={transactionForm.description}
                    readOnly={transactionForm.isPayrollMirror}
                    onChange={(e) =>
                      setTransactionForm((current) => ({
                        ...current,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Ex.: Restaurante, aluguel, salário, gasolina"
                    required={!transactionForm.isPayrollMirror}
                  />
                </div>
                <div className="form-group">
                  <label>Valor</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={transactionForm.amount}
                    readOnly={transactionForm.isPayrollMirror}
                    onChange={(e) =>
                      setTransactionForm((current) => ({
                        ...current,
                        amount: e.target.value,
                      }))
                    }
                    required={!transactionForm.isPayrollMirror}
                  />
                </div>
              </div>

              <div className="finance-form-grid finance-form-grid-4">
                <div className="form-group">
                  <label>Data</label>
                  <input
                    type="date"
                    value={transactionForm.transactionDate}
                    readOnly={transactionForm.isPayrollMirror}
                    onChange={(e) =>
                      setTransactionForm((current) => ({
                        ...current,
                        transactionDate: e.target.value,
                      }))
                    }
                    required={!transactionForm.isPayrollMirror}
                  />
                </div>
                <PremiumSelect
                  label="Período"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.cycleId}
                  options={cycleFormSelectOptions}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      cycleId: value,
                    }))
                  }
                  placeholder="Sem vínculo"
                />
                <PremiumSelect
                  label="Conta / meio"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.accountId}
                  options={accountPickOptions}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      accountId: value,
                    }))
                  }
                  placeholder="Não informado"
                />
                <PremiumSelect
                  label="Banco"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.bankId}
                  options={bankPickOptions}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      bankId: value,
                    }))
                  }
                  placeholder="Não informado"
                />
                <PremiumSelect
                  label="Fonte"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.sourceId}
                  options={sourcePickOptions}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      sourceId: value,
                    }))
                  }
                  placeholder="Não informado"
                />
              </div>

              <div className="finance-form-grid finance-form-grid-3">
                <PremiumSelect
                  label="Categoria"
                  wrapperClassName="premium-select-field"
                  value={transactionForm.categoryId}
                  options={categoryPickOptions}
                  onChange={(value) =>
                    setTransactionForm((current) => ({
                      ...current,
                      categoryId: value,
                    }))
                  }
                  placeholder="Não informado"
                />
                <div className="form-group finance-form-span-2">
                  <label>Observações</label>
                  <input
                    type="text"
                    value={transactionForm.notes}
                    onChange={(e) =>
                      setTransactionForm((current) => ({
                        ...current,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Detalhes adicionais do lançamento"
                  />
                </div>
              </div>

              {!transactionForm.isPayrollMirror && (
              <div className="form-group finance-attachment-block">
                <div className="finance-attachment-inner">
                  <div className="finance-attachment-head">
                    <label
                      className="finance-attachment-title"
                      htmlFor="finance-tx-attachment-input"
                    >
                      Comprovante
                    </label>
                    <span className="finance-attachment-badge">Opcional</span>
                  </div>
                  <div
                    className={`finance-upload-shell${attachmentDropzoneActive ? ' is-dragging' : ''}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current += 1;
                      setAttachmentDropzoneActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current -= 1;
                      if (attachmentDragDepthRef.current <= 0) {
                        attachmentDragDepthRef.current = 0;
                        setAttachmentDropzoneActive(false);
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current = 0;
                      setAttachmentDropzoneActive(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        setTransactionAttachmentFile(file);
                        setTransactionForm((current) => ({ ...current, removeAttachment: false }));
                      }
                    }}
                  >
                    <div className="finance-upload-panel">
                      <input
                        id="finance-tx-attachment-input"
                        ref={transactionAttachmentInputRef}
                        className="finance-upload-input-native"
                        type="file"
                        accept="application/pdf,image/*,.csv,.txt,.xls,.xlsx,.doc,.docx"
                        onChange={(ev) => {
                          const file = ev.target.files?.[0] ?? null;
                          setTransactionAttachmentFile(file);
                          if (file) {
                            setTransactionForm((current) => ({ ...current, removeAttachment: false }));
                          }
                        }}
                      />
                      {!transactionAttachmentFile ? (
                        <div className="finance-upload-empty">
                          <div className="finance-upload-icon-wrap" aria-hidden>
                            <svg
                              className="finance-upload-icon"
                              viewBox="0 0 48 48"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M24 10v20M24 10l-8 8m8-8l8 8"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 34h24"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M10 38h28"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                opacity="0.35"
                              />
                            </svg>
                          </div>
                          <p className="finance-upload-lead">
                            Solte o arquivo aqui ou escolha no dispositivo
                          </p>
                          <p className="finance-upload-meta">
                            PDF, imagens, Office, CSV ou texto · até 15 MB
                          </p>
                          <button
                            type="button"
                            className="finance-upload-cta"
                            onClick={() => transactionAttachmentInputRef.current?.click()}
                          >
                            Escolher arquivo
                          </button>
                        </div>
                      ) : (
                        <div className="finance-upload-filled">
                          <div className="finance-upload-file-icon" aria-hidden>
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 2v6h6M9 15h6M9 11h6"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="finance-upload-file-body">
                            <span className="finance-upload-file-name">
                              {transactionAttachmentFile.name}
                            </span>
                            <span className="finance-upload-file-size">
                              {formatAttachmentFileSize(transactionAttachmentFile.size)}
                            </span>
                          </div>
                          <div className="finance-upload-file-actions">
                            <button
                              type="button"
                              className="finance-upload-link"
                              onClick={() => transactionAttachmentInputRef.current?.click()}
                            >
                              Trocar
                            </button>
                            <button
                              type="button"
                              className="finance-upload-link is-danger"
                              onClick={() => {
                                setTransactionAttachmentFile(null);
                                if (transactionAttachmentInputRef.current) {
                                  transactionAttachmentInputRef.current.value = '';
                                }
                              }}
                            >
                              Limpar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {editingTransactionId &&
                    transactionForm.existingAttachmentName &&
                    !transactionForm.removeAttachment &&
                    !transactionAttachmentFile && (
                      <div className="finance-attachment-existing finance-attachment-existing-premium">
                        <div className="finance-attachment-existing-icon" aria-hidden>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M12 3v10m0 0l4-4m-4 4L8 9"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div className="finance-attachment-existing-text">
                          <span className="finance-attachment-existing-label">
                            Arquivo no servidor
                          </span>
                          <strong>{transactionForm.existingAttachmentName}</strong>
                        </div>
                        <button
                          type="button"
                          className="finance-upload-chip-remove"
                          onClick={() => {
                            setTransactionForm((current) => ({
                              ...current,
                              removeAttachment: true,
                            }));
                            setTransactionAttachmentFile(null);
                            if (transactionAttachmentInputRef.current) {
                              transactionAttachmentInputRef.current.value = '';
                            }
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  {editingTransactionId &&
                    transactionForm.existingAttachmentName &&
                    !transactionForm.removeAttachment &&
                    Boolean(transactionAttachmentFile) && (
                      <p className="finance-field-hint finance-attachment-replace-hint">
                        O novo arquivo substitui o comprovante atual ao salvar.
                      </p>
                    )}
                  {editingTransactionId && transactionForm.removeAttachment && (
                    <p className="finance-field-hint finance-attachment-removed-hint">
                      O anexo será removido ao salvar (ou envie um novo arquivo para substituir).
                    </p>
                  )}
                </div>
              </div>
              )}

              <div className="finance-tag-selector">
                <span>Tags</span>
                <div className="finance-tag-options">
                  {tags.map((tag) => {
                    const checked = transactionForm.tagIds.includes(tag.id);
                    return (
                      <label
                        key={tag.id}
                        className={`finance-tag-chip${checked ? ' is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setTransactionForm((current) => ({
                              ...current,
                              tagIds: checked
                                ? current.tagIds.filter((tagId) => tagId !== tag.id)
                                : [...current.tagIds, tag.id],
                            }))
                          }
                        />
                        <span style={{ backgroundColor: tag.color }} className="finance-tag-color" />
                        {tag.name}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="finance-form-footer">
                <button
                  type="button"
                  className="finance-action-button is-muted"
                  onClick={resetTransactionForm}
                >
                  Limpar formulário
                </button>
                <button type="submit" className="finance-action-button">
                  {transactionForm.isPayrollMirror
                    ? 'Salvar classificação'
                    : editingTransactionId
                      ? 'Salvar lançamento'
                      : 'Cadastrar lançamento'}
                </button>
              </div>
            </form>
          </section>

          <section className="finance-surface">
            <div className="finance-section-header">
              <div>
                <span className="finance-section-kicker">Períodos</span>
                <h2>Agrupado por período</h2>
              </div>
              <p>{groupedTransactions.length} grupo(s) no recorte selecionado.</p>
            </div>

            {groupedTransactions.length === 0 ? (
              <div className="finance-empty">
                <strong>Nenhum lançamento encontrado</strong>
                <p>Cadastre receitas e despesas ou ajuste os filtros acima.</p>
              </div>
            ) : (
              <div className="finance-period-list">
                {groupedTransactions.map((group) => (
                  <article className="finance-period-card" key={group.key}>
                    <div className="finance-period-header">
                      <div>
                        <strong>{group.label}</strong>
                        <span>{group.transactions.length} lançamento(s)</span>
                      </div>
                    </div>

                    <div className="finance-transaction-list">
                      {group.transactions.map((transaction) => (
                        <div className="finance-transaction-card" key={transaction.id}>
                          <div className="finance-transaction-main">
                            <div>
                              <span
                                className={`finance-type-pill ${resolveTransactionDisplayType(transaction)}`}
                              >
                                {
                                  transactionTypeLabels[
                                    resolveTransactionDisplayType(transaction)
                                  ]
                                }
                              </span>
                              <span
                                className={`finance-approval-pill ${transaction.approvalStatus || 'approved'}`}
                              >
                                {
                                  transactionApprovalLabels[
                                    transaction.approvalStatus || 'approved'
                                  ]
                                }
                              </span>
                              <strong>{transaction.description}</strong>
                              <small>
                                {formatDate(transaction.transactionDate)}
                                {transaction.createdByUser?.name
                                  ? ` • Enviado por ${transaction.createdByUser.name}`
                                  : ''}
                                {transaction.account?.name ? ` • ${transaction.account.name}` : ''}
                                {transaction.bank?.name
                                  ? ` • ${transaction.bank.name}${
                                      transaction.bank.accountNumber
                                        ? ` (${transaction.bank.accountNumber})`
                                        : ''
                                    }`
                                  : ''}
                                {transaction.source?.name ? ` • ${transaction.source.name}` : ''}
                              </small>
                            </div>
                            <div className="finance-transaction-amount">
                              {formatCurrency(transaction.amount)}
                            </div>
                          </div>

                          <div className="finance-transaction-meta">
                            <span>{transaction.category?.name || 'Sem categoria'}</span>
                            <div className="finance-inline-tags">
                              {transaction.tags.map((tag) => (
                                <span key={tag.id} className="finance-inline-tag">
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="finance-card-actions">
                            {transaction.payrollLineId ? (
                              <>
                                <span className="finance-inline-tag">Folha de pagamento</span>
                                {isFinanceAdmin ? (
                                  <button
                                    type="button"
                                    className="finance-card-button"
                                    onClick={() => {
                                      setEditingTransactionId(transaction.id);
                                      setTransactionForm({
                                        type: transaction.type,
                                        description: transaction.description,
                                        amount: String(transaction.amount),
                                        transactionDate: normalizeDateInput(
                                          transaction.transactionDate,
                                        ),
                                        cycleId: transaction.cycleId || '',
                                        accountId: transaction.accountId || '',
                                        bankId: transaction.bankId || '',
                                        sourceId: transaction.sourceId || '',
                                        categoryId: transaction.categoryId || '',
                                        notes: transaction.notes || '',
                                        tagIds: transaction.tags.map((tag) => tag.id),
                                        existingAttachmentName: null,
                                        removeAttachment: false,
                                        isPayrollMirror: true,
                                      });
                                      setTransactionAttachmentFile(null);
                                      if (transactionAttachmentInputRef.current) {
                                        transactionAttachmentInputRef.current.value = '';
                                      }
                                      focusTransactionEditor();
                                    }}
                                  >
                                    Classificar para relatórios
                                  </button>
                                ) : null}
                                <span className="finance-payroll-readonly-text">
                                  Valor e descrição vêm da{' '}
                                  <Link to="/financeiro/folha">Folha de pagamento</Link>.
                                </span>
                              </>
                            ) : (
                              <>
                                {transaction.attachmentOriginalName && (
                                  <button
                                    type="button"
                                    className="finance-card-button"
                                    onClick={() =>
                                      downloadTransactionAttachment(
                                        transaction.id,
                                        transaction.attachmentOriginalName || 'comprovante',
                                      )
                                    }
                                  >
                                    Baixar comprovante
                                  </button>
                                )}
                                {isFinanceAdmin ? (
                                  <>
                                    {isTransactionPending(transaction) ? (
                                      <>
                                        <button
                                          type="button"
                                          className="finance-card-button is-approve"
                                          onClick={() => void handleApproveTransaction(transaction)}
                                        >
                                          Aprovar
                                        </button>
                                        <button
                                          type="button"
                                          className="finance-card-button is-reject"
                                          onClick={() => void handleRejectTransaction(transaction)}
                                        >
                                          Recusar
                                        </button>
                                      </>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="finance-card-button"
                                      onClick={() => {
                                        setEditingTransactionId(transaction.id);
                                        setTransactionForm({
                                          type: transaction.type,
                                          description: transaction.description,
                                          amount: String(transaction.amount),
                                          transactionDate: normalizeDateInput(transaction.transactionDate),
                                          cycleId: transaction.cycleId || '',
                                          accountId: transaction.accountId || '',
                                          bankId: transaction.bankId || '',
                                          sourceId: transaction.sourceId || '',
                                          categoryId: transaction.categoryId || '',
                                          notes: transaction.notes || '',
                                          tagIds: transaction.tags.map((tag) => tag.id),
                                          existingAttachmentName:
                                            transaction.attachmentOriginalName || null,
                                          removeAttachment: false,
                                          isPayrollMirror: false,
                                        });
                                        setTransactionAttachmentFile(null);
                                        if (transactionAttachmentInputRef.current) {
                                          transactionAttachmentInputRef.current.value = '';
                                        }
                                        focusTransactionEditor();
                                      }}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      className="finance-card-button is-danger"
                                      onClick={() =>
                                        handleDelete(
                                          `/finance/transactions/${transaction.id}`,
                                          transaction.description,
                                          'Lançamento excluído com sucesso!',
                                          resetTransactionForm,
                                        )
                                      }
                                    >
                                      Excluir
                                    </button>
                                  </>
                                ) : (
                                  <span className="finance-payroll-readonly-text">
                                    {transaction.approvalStatus === 'rejected'
                                      ? 'Lançamento recusado pelo administrador.'
                                      : transaction.approvalStatus === 'approved'
                                        ? 'Lançamento aprovado pelo administrador.'
                                        : 'Lançamento em análise. Apenas o administrador pode aprovar ou recusar.'}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
      </div>

        <section
          ref={accountSectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'accounts'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Contas</span>
              <h2>{editingAccountId ? 'Editar conta' : 'Cadastrar conta / meio financeiro'}</h2>
            </div>
            <p>Ex.: caixa, banco, cartão ou carteira digital.</p>
          </div>

          <form className="finance-form" onSubmit={handleAccountSubmit}>
            <div className="finance-form-grid finance-form-grid-3">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                  required
                />
              </div>
              <PremiumSelect
                label="Tipo"
                wrapperClassName="premium-select-field"
                value={accountForm.type}
                options={accountTypeSelectOptions}
                onChange={(value) =>
                  setAccountForm({ ...accountForm, type: value as FinanceAccountType })
                }
              />
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={accountForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) =>
                  setAccountForm({ ...accountForm, active: value === 'true' })
                }
              />
            </div>

            <div className="finance-form-grid">
              <div className="form-group">
                <label>Descrição</label>
                <input
                  type="text"
                  value={accountForm.description}
                  onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                />
              </div>
            </div>

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetAccountForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingAccountId ? 'Salvar conta' : 'Cadastrar conta'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {accounts.map((account) => (
              <article className="finance-info-card" key={account.id}>
                <strong>{account.name}</strong>
                <span>{accountTypeLabels[account.type]}</span>
                <p>{account.description || 'Sem descrição'}</p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingAccountId(account.id);
                      setAccountForm({
                        name: account.name,
                        type: account.type,
                        description: account.description || '',
                        active: account.active,
                      });
                      scrollToSection(accountSectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/accounts/${account.id}`,
                        `a conta ${account.name}`,
                        'Conta excluída com sucesso!',
                        resetAccountForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          ref={bankSectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'banks'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Bancos</span>
              <h2>{editingBankId ? 'Editar banco' : 'Cadastrar instituição bancária'}</h2>
            </div>
            <p>
              Cadastre os bancos pelos quais as operações acontecem (ex.: Nubank, Bradesco, Caixa).
              Depois vincule cada lançamento ao banco correspondente.
            </p>
          </div>

          <form className="finance-form" onSubmit={handleBankSubmit}>
            <div className="finance-form-grid finance-form-grid-3">
              <div className="form-group">
                <label>Nome do banco</label>
                <input
                  type="text"
                  value={bankForm.name}
                  onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
                  placeholder="Ex.: Banco do Brasil"
                  required
                />
              </div>
              <div className="form-group">
                <label>Código (opcional)</label>
                <input
                  type="text"
                  value={bankForm.code}
                  onChange={(e) => setBankForm({ ...bankForm, code: e.target.value })}
                  placeholder="Ex.: 001"
                />
              </div>
              <div className="form-group">
                <label>Número da conta</label>
                <input
                  type="text"
                  value={bankForm.accountNumber}
                  onChange={(e) =>
                    setBankForm({ ...bankForm, accountNumber: e.target.value })
                  }
                  placeholder="Ex.: 12345-6"
                />
              </div>
            </div>

            <div className="finance-form-grid finance-form-grid-3">
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={bankForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) =>
                  setBankForm({ ...bankForm, active: value === 'true' })
                }
              />
              <div className="form-group finance-form-span-2">
                <label>Descrição</label>
                <input
                  type="text"
                  value={bankForm.description}
                  onChange={(e) => setBankForm({ ...bankForm, description: e.target.value })}
                  placeholder="Agência, titular ou observações"
                />
              </div>
            </div>

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetBankForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingBankId ? 'Salvar banco' : 'Cadastrar banco'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {banks.map((bank) => (
              <article className="finance-info-card" key={bank.id}>
                <strong>{bank.name}</strong>
                <span>
                  {[
                    bank.code ? `Código ${bank.code}` : null,
                    bank.accountNumber ? `Conta ${bank.accountNumber}` : null,
                  ]
                    .filter(Boolean)
                    .join(' • ') || 'Sem código / conta'}
                </span>
                <p>{bank.description || 'Sem descrição'}</p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingBankId(bank.id);
                      setBankForm({
                        name: bank.name,
                        code: bank.code || '',
                        accountNumber: bank.accountNumber || '',
                        description: bank.description || '',
                        active: bank.active,
                      });
                      scrollToSection(bankSectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/banks/${bank.id}`,
                        `o banco ${bank.name}`,
                        'Banco excluído com sucesso!',
                        resetBankForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          ref={sourceSectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'sources'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Fontes</span>
              <h2>{editingSourceId ? 'Editar fonte' : 'Cadastrar fonte de receita / despesa'}</h2>
            </div>
            <p>Fontes representam a origem ou destino financeiro do lançamento.</p>
          </div>

          <form className="finance-form" onSubmit={handleSourceSubmit}>
            <div className="finance-form-grid finance-form-grid-3">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={sourceForm.name}
                  onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                  required
                />
              </div>
              <PremiumSelect
                label="Tipo"
                wrapperClassName="premium-select-field"
                value={sourceForm.type}
                options={transactionFormTypeOptions}
                onChange={(value) =>
                  setSourceForm({
                    ...sourceForm,
                    type: value as FinanceTransactionType,
                  })
                }
              />
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={sourceForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) =>
                  setSourceForm({ ...sourceForm, active: value === 'true' })
                }
              />
            </div>

            <div className="finance-form-grid">
              <div className="form-group">
                <label>Descrição</label>
                <input
                  type="text"
                  value={sourceForm.description}
                  onChange={(e) => setSourceForm({ ...sourceForm, description: e.target.value })}
                />
              </div>
            </div>

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetSourceForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingSourceId ? 'Salvar fonte' : 'Cadastrar fonte'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {sources.map((source) => (
              <article className="finance-info-card" key={source.id}>
                <strong>{source.name}</strong>
                <span>{transactionTypeLabels[source.type]}</span>
                <p>{source.description || 'Sem descrição'}</p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingSourceId(source.id);
                      setSourceForm({
                        name: source.name,
                        type: source.type,
                        description: source.description || '',
                        active: source.active,
                      });
                      scrollToSection(sourceSectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/sources/${source.id}`,
                        `a fonte ${source.name}`,
                        'Fonte excluída com sucesso!',
                        resetSourceForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          ref={categorySectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'categories'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Categorias</span>
              <h2>{editingCategoryId ? 'Editar categoria' : 'Cadastrar classificação'}</h2>
            </div>
            <p>Use macro, médio e micro para estruturar a visão financeira da operação.</p>
          </div>

          <form className="finance-form" onSubmit={handleCategorySubmit}>
            <div className="finance-form-grid finance-form-grid-4">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                />
              </div>
              <PremiumSelect
                label="Tipo"
                wrapperClassName="premium-select-field"
                value={categoryForm.type}
                options={transactionFormTypeOptions}
                onChange={(value) =>
                  setCategoryForm({
                    ...categoryForm,
                    type: value as FinanceTransactionType,
                    parentId: '',
                  })
                }
              />
              <PremiumSelect
                label="Nível"
                wrapperClassName="premium-select-field"
                value={categoryForm.level}
                options={categoryLevelSelectOptions}
                onChange={(value) =>
                  setCategoryForm({
                    ...categoryForm,
                    level: value as FinanceCategoryLevel,
                    parentId: '',
                  })
                }
              />
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={categoryForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) =>
                  setCategoryForm({ ...categoryForm, active: value === 'true' })
                }
              />
            </div>

            {categoryForm.level !== 'macro' && (
              <div className="finance-form-grid">
                <PremiumSelect
                  label="Categoria pai"
                  wrapperClassName="premium-select-field"
                  value={categoryForm.parentId}
                  options={categoryParentPickOptions}
                  onChange={(value) => setCategoryForm({ ...categoryForm, parentId: value })}
                  placeholder="Selecione"
                  required
                />
              </div>
            )}

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetCategoryForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingCategoryId ? 'Salvar categoria' : 'Cadastrar categoria'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {categories.map((category) => (
              <article className="finance-info-card" key={category.id}>
                <strong>{category.name}</strong>
                <span>
                  {transactionTypeLabels[category.type]} • {categoryLevelLabels[category.level]}
                </span>
                <p>{category.parent?.name ? `Pai: ${category.parent.name}` : 'Sem categoria pai'}</p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingCategoryId(category.id);
                      setCategoryForm({
                        name: category.name,
                        type: category.type,
                        level: category.level,
                        parentId: category.parentId || '',
                        active: category.active,
                      });
                      scrollToSection(categorySectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/categories/${category.id}`,
                        `a categoria ${category.name}`,
                        'Categoria excluída com sucesso!',
                        resetCategoryForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

      {isFinanceAdmin ? (
        <section
          ref={tagSectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'tags'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Tags</span>
              <h2>{editingTagId ? 'Editar tag' : 'Cadastrar tag'}</h2>
            </div>
            <p>As tags funcionam como marcadores manuais complementares para busca e organização.</p>
          </div>

          <form className="finance-form" onSubmit={handleTagSubmit}>
            <div className="finance-form-grid finance-form-grid-3">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Cor</label>
                <input
                  type="color"
                  value={tagForm.color}
                  onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                />
              </div>
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={tagForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) => setTagForm({ ...tagForm, active: value === 'true' })}
              />
            </div>

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetTagForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingTagId ? 'Salvar tag' : 'Cadastrar tag'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {tags.map((tag) => (
              <article className="finance-info-card" key={tag.id}>
                <strong>{tag.name}</strong>
                <span className="finance-color-preview">
                  <span style={{ backgroundColor: tag.color }} className="finance-tag-color" />
                  {tag.color}
                </span>
                <p>{tag.active ? 'Tag ativa' : 'Tag inativa'}</p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingTagId(tag.id);
                      setTagForm({
                        name: tag.name,
                        color: tag.color,
                        active: tag.active,
                      });
                      scrollToSection(tagSectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/tags/${tag.id}`,
                        `a tag ${tag.name}`,
                        'Tag excluída com sucesso!',
                        resetTagForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {isFinanceAdmin ? (
        <section
          ref={keywordSectionRef}
          className="finance-surface premium-tab-panel"
          hidden={activeTab !== 'keywords'}
        >
          <div className="finance-section-header">
            <div>
              <span className="finance-section-kicker">Palavras-chave</span>
              <h2>{editingKeywordId ? 'Editar palavra-chave' : 'Cadastrar regra de sugestão'}</h2>
            </div>
            <p>
              As palavras-chave ajudam a sugerir automaticamente categoria, fonte e tag ao criar
              lançamentos.
            </p>
          </div>

          <form className="finance-form" onSubmit={handleKeywordSubmit}>
            <div className="finance-form-grid finance-form-grid-4">
              <div className="form-group">
                <label>Termo</label>
                <input
                  type="text"
                  value={keywordForm.term}
                  onChange={(e) => setKeywordForm({ ...keywordForm, term: e.target.value })}
                  placeholder="Ex.: restaurante, gasolina, salário"
                  required
                />
              </div>
              <PremiumSelect
                label="Escopo"
                wrapperClassName="premium-select-field"
                value={keywordForm.scope}
                options={keywordScopeSelectOptions}
                onChange={(value) =>
                  setKeywordForm({
                    ...keywordForm,
                    scope: value as FinanceKeywordScope,
                    categoryId: '',
                    sourceId: '',
                  })
                }
              />
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={keywordForm.active ? 'true' : 'false'}
                options={activeStatusOptions}
                onChange={(value) =>
                  setKeywordForm({ ...keywordForm, active: value === 'true' })
                }
              />
            </div>

            <div className="finance-form-grid finance-form-grid-3">
              <PremiumSelect
                label="Categoria sugerida"
                wrapperClassName="premium-select-field"
                value={keywordForm.categoryId}
                options={keywordCategoryPickOptions}
                onChange={(value) => setKeywordForm({ ...keywordForm, categoryId: value })}
                placeholder="Nenhuma"
              />
              <PremiumSelect
                label="Fonte sugerida"
                wrapperClassName="premium-select-field"
                value={keywordForm.sourceId}
                options={keywordSourcePickOptions}
                onChange={(value) => setKeywordForm({ ...keywordForm, sourceId: value })}
                placeholder="Nenhuma"
              />
              <PremiumSelect
                label="Tag sugerida"
                wrapperClassName="premium-select-field"
                value={keywordForm.tagId}
                options={keywordTagPickOptions}
                onChange={(value) => setKeywordForm({ ...keywordForm, tagId: value })}
                placeholder="Nenhuma"
              />
            </div>

            <div className="finance-form-footer">
              <button type="button" className="finance-action-button is-muted" onClick={resetKeywordForm}>
                Limpar
              </button>
              <button type="submit" className="finance-action-button">
                {editingKeywordId ? 'Salvar palavra-chave' : 'Cadastrar palavra-chave'}
              </button>
            </div>
          </form>

          <div className="finance-card-grid">
            {keywords.map((keyword) => (
              <article className="finance-info-card" key={keyword.id}>
                <strong>{keyword.term}</strong>
                <span>{keywordScopeLabels[keyword.scope]}</span>
                <p>
                  Categoria: {keyword.category?.name || 'Nenhuma'} • Fonte:{' '}
                  {keyword.source?.name || 'Nenhuma'} • Tag: {keyword.tag?.name || 'Nenhuma'}
                </p>
                <div className="finance-card-actions">
                  <button
                    type="button"
                    className="finance-card-button"
                    onClick={() => {
                      setEditingKeywordId(keyword.id);
                      setKeywordForm({
                        term: keyword.term,
                        scope: keyword.scope,
                        categoryId: keyword.categoryId || '',
                        sourceId: keyword.sourceId || '',
                        tagId: keyword.tagId || '',
                        active: keyword.active,
                      });
                      scrollToSection(keywordSectionRef.current);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="finance-card-button is-danger"
                    onClick={() =>
                      handleDelete(
                        `/finance/keywords/${keyword.id}`,
                        `a palavra-chave ${keyword.term}`,
                        'Palavra-chave excluída com sucesso!',
                        resetKeywordForm,
                      )
                    }
                  >
                    Excluir
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert((current) => ({ ...current, isOpen: false }))}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={Boolean(confirmDialog?.isOpen)}
        title={confirmDialog?.title}
        message={confirmDialog?.message || ''}
        confirmLabel="Excluir"
        isLoading={isConfirmingDelete}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Finance;
